import type {
  Benefit,
  Membership,
  PurchasablePlan,
} from '@wix/ambassador-ecom-memberships-spi-host-v1-membership/types';
import {
  BalanceType,
  LimitedQuotaType,
  QuotaType,
} from '@wix/ambassador-ecom-memberships-spi-host-v1-membership/types';
import { BenefitType } from '@wix/ambassador-pricing-plans-v2-benefit/types';
import type { DateTimeFormatter } from '@wix/bookings-date-time';
import type { PricingVariantIdsByPlanId } from '@wix/pricing-plans-tpa-api';
import type { Experiments } from '@wix/yoshi-flow-editor';
import { ExperimentsConsts } from '../../../consts/experiments';
import {
  PaymentMethod,
  type PaymentOption,
  PaymentType,
  type PlanInfoWithDescription,
  ReservedPaymentOptionIds,
  ReservedPaymentOptionLabels,
  type TFunction,
} from '../../../types/types';
import {
  OfferedAsType,
  ServicePayment,
} from '../../mappers/serviceMapper/utils/payment.mapper';

export const isOfferedAsOneTimeOnly = (servicePayment: ServicePayment) =>
  servicePayment.offeredAs.length === 1 && isOfferedAsOneTime(servicePayment);

export const isOfferedAsOneTime = (servicePayment: ServicePayment) =>
  servicePayment.offeredAs.includes(OfferedAsType.ONE_TIME);

export const isOfferedAsPricingPlanOnly = (servicePayment: ServicePayment) =>
  servicePayment.offeredAs.length <= 1 &&
  isServiceHavePricingPlans(servicePayment);

export const getBenefitType = (membership: Membership) =>
  membership.credits ? BenefitType.LIMITED : BenefitType.UNLIMITED;

export const isServiceHavePricingPlans = (servicePayment: ServicePayment) =>
  servicePayment.pricingPlanInfo?.pricingPlans?.length! > 0;

export const isOfferedAsPricingPlanOnlyWithoutPlansConnected = (
  servicePayment: ServicePayment,
) =>
  servicePayment.offeredAs.length === 1 &&
  servicePayment.offeredAs[0] === OfferedAsType.PRICING_PLAN &&
  !isServiceHavePricingPlans(servicePayment);

// Two variants of one plan are two distinct purchases, so identify one by this, not by plan id
const buildPlanVariantKey = (
  planId: string | undefined | null,
  pricingVariantId?: string,
): string => [planId, pricingVariantId].filter(Boolean).join(':');

export const createBuyPlanBenefitOption = (
  planInfo: PlanInfoWithDescription,
): PaymentOption => ({
  id: planInfo?.plan?.id!,
  label: planInfo?.plan?.name!,
  suffix: planInfo.suffix,
  disabled: false,
  type: PaymentType.BUY_PLAN,
  pricingDetails: planInfo.description,
});

export const createBuyPlanOptionPerVariant = (
  planInfo: PlanInfoWithDescription,
): PaymentOption[] => {
  const { pricingVariantsDetails } = planInfo;
  const planId = planInfo.plan?.id;

  if (!planId || !pricingVariantsDetails?.length) {
    return [];
  }

  const planName = planInfo.plan?.name ?? '';
  // A single-variant plan is offered as the whole plan, without its category.
  const isSingleVariantPlan = pricingVariantsDetails.length === 1;

  return pricingVariantsDetails.map(
    ({ pricingVariantId, categoryName, suffix, description }) => ({
      id: buildPlanVariantKey(planId, pricingVariantId),
      label: isSingleVariantPlan
        ? planName
        : [planName, categoryName].filter(Boolean).join(' | '),
      suffix,
      disabled: false,
      type: PaymentType.BUY_PLAN,
      pricingDetails: description,
      planId,
      pricingVariantId,
    }),
  );
};

// A BUY_PLAN option stores planId/pricingVariantId as fields.
export const buildBuyPlanPricingVariantIdsByPlanId = (
  selectedBuyPlanOptions: PaymentOption[],
): PricingVariantIdsByPlanId | undefined =>
  buildPricingVariantIdsByPlanId(
    selectedBuyPlanOptions.map(({ planId, pricingVariantId }) => ({
      planId,
      pricingVariantId,
    })),
  );

// Selections missing either id can't be represented in the map and are dropped.
const buildPricingVariantIdsByPlanId = (
  selectedVariants: {
    planId?: string | null;
    pricingVariantId?: string | null;
  }[],
): PricingVariantIdsByPlanId | undefined => {
  const pricingVariantIdsByPlanId =
    selectedVariants.reduce<PricingVariantIdsByPlanId>(
      (variantIdsByPlanId, { planId, pricingVariantId }) => {
        if (!planId || !pricingVariantId) {
          return variantIdsByPlanId;
        }

        const variantIds = variantIdsByPlanId[planId] ?? [];

        return variantIds.includes(pricingVariantId)
          ? variantIdsByPlanId
          : {
              ...variantIdsByPlanId,
              [planId]: [...variantIds, pricingVariantId],
            };
      },
      {},
    );

  return Object.keys(pricingVariantIdsByPlanId).length
    ? pricingVariantIdsByPlanId
    : undefined;
};

export const buildBuyPlanAndBookOptionId = (
  catalogItemId: string | undefined,
  benefitId: string | undefined,
): string => `BUY_PLAN_AND_BOOK:${catalogItemId}:${benefitId}`;

export function createBuyPlanAndBookOption({
  plan,
  benefit,
  redemptionCost,
  numberOfParticipants,
  t,
}: {
  plan: PurchasablePlan;
  benefit: Benefit;
  redemptionCost: number;
  numberOfParticipants: number;
  t: TFunction;
}): PaymentOption {
  const planName = plan.name?.translated ?? plan.name?.original ?? '';
  const hasMultipleBenefits = (plan.benefits?.length ?? 0) > 1;
  const label =
    hasMultipleBenefits && benefit.label
      ? `${planName} | ${benefit.label}`
      : planName;

  const isLimited = benefit.quotaType === QuotaType.LIMITED;
  const isSessionBased =
    benefit.limitedQuota?.limitedQuotaType === LimitedQuotaType.PUNCH_CARD;
  const total = benefit.limitedQuota?.total;
  const creditCost = redemptionCost || 1;

  // Subtitle ("additional info"): the granted amount (structured, from the
  // benefit quota), the provider's `offeringDescription` (validity / setup fee /
  // free trial), and the per-booking cost. Per Figma, the price/recurring-price
  // goes in the suffix and everything else lives here.
  const includedLine =
    isLimited && total
      ? t(
          isSessionBased
            ? 'app.payment.purchasable-plan.sessions-included'
            : 'app.payment.purchasable-plan.credits-included',
          { total },
        )
      : undefined;
  const perBookingLine =
    isLimited && !isSessionBased && redemptionCost
      ? t('app.payment.purchasable-plan.credits-per-session', {
          cost: redemptionCost,
        })
      : undefined;
  const pricingDetails =
    [includedLine, plan.offeringDescription, perBookingLine]
      .filter(Boolean)
      .join(', ') || undefined;

  const disabled = isLimited
    ? numberOfParticipants * creditCost > (total ?? 0)
    : numberOfParticipants > 1;

  return {
    id: buildBuyPlanAndBookOptionId(
      plan.catalogReference?.catalogItemId,
      benefit.benefitId,
    ),
    label,
    // The suffix is the formatted, display-ready recurring price (e.g. "€14/Month"),
    // provided by Pricing Plans as `priceDescription`. The bare `price` decimal has
    // no currency/cycle, so it can't be formatted here. `offeringDescription` is the
    // subtitle "additional info" (validity / fees / trial), NOT the price.
    suffix: plan.priceDescription ?? undefined,
    pricingDetails,
    disabled,
    creditRemain: isLimited ? total : undefined,
    creditCost,
    type: PaymentType.BUY_PLAN_AND_BOOK,
    purchasablePlanRef: plan.catalogReference,
    purchasablePlanBenefitId: benefit.benefitId,
    purchasablePlanAppId: plan.catalogReference?.appId,
    purchasablePlanInfoCollectionUrl: plan.infoCollectionUrl ?? undefined,
    purchasablePlanName: plan.name,
    purchasablePlanPrice: plan.price,
  };
}

export const createPayOfflineOption = (
  price: string,
  t: TFunction,
  discountRuleName?: string,
  isCart?: boolean,
): PaymentOption => {
  const payOfflineOption: PaymentOption = {
    id: ReservedPaymentOptionIds.SingleSession,
    label: ReservedPaymentOptionLabels.OFFLINE,
    disabled: false,
    type: PaymentType.SINGLE_SESSION,
    paymentMethod: PaymentMethod.OFFLINE,
    suffix: price,
    // With a discount, the subtitle is the discount name alone — the (catalog) price
    // already shows in the suffix, so no separate price line.
    pricingDetails: discountRuleName
      ? undefined
      : t('app.bookings-form.payment-section.service-dropdown.pay-offline'),
    discountDetails: {
      name: discountRuleName,
      isCart,
    },
  };

  return payOfflineOption;
};

export const createPayOnlineOption = (
  price: string,
  t: TFunction,
  discountRuleName?: string,
  isCart?: boolean,
): PaymentOption => {
  const payOnlineOption: PaymentOption = {
    id: ReservedPaymentOptionIds.SingleSession,
    label: ReservedPaymentOptionLabels.ONLINE,
    disabled: false,
    type: PaymentType.SINGLE_SESSION,
    paymentMethod: PaymentMethod.ONLINE,
    suffix: price,
    // With a discount, the subtitle is the discount name alone — the (catalog) price
    // already shows in the suffix, so no separate price line.
    pricingDetails: discountRuleName
      ? undefined
      : t('app.bookings-form.payment-section.service-dropdown.pay-online'),
    discountDetails: {
      name: discountRuleName,
      isCart,
    },
  };

  return payOnlineOption;
};

// Free and custom-price options are both reached only from the OFFLINE branch of
// getSlotServicePaymentOptions (no online payment is collected for either).
export const createFreeOption = (): PaymentOption => ({
  id: ReservedPaymentOptionIds.SingleSession,
  label: ReservedPaymentOptionLabels.FREE,
  disabled: false,
  type: PaymentType.SINGLE_SESSION,
  paymentMethod: PaymentMethod.OFFLINE,
});

export const createCustomPriceOption = (
  customPrice: string,
): PaymentOption => ({
  id: ReservedPaymentOptionIds.SingleSession,
  label: customPrice,
  disabled: false,
  type: PaymentType.SINGLE_SESSION,
  paymentMethod: PaymentMethod.OFFLINE,
});

export function createUsePlanOption(
  membership: Membership,
  numberOfParticipants: number,
  t: TFunction,
  dateAndTimeFormatter: DateTimeFormatter,
  disabledReason: string | undefined,
): PaymentOption {
  const disabled =
    Boolean(disabledReason) ||
    isDisabledMembership({
      membership,
      numberOfParticipants,
    });

  const isSessionBased =
    membership?.credits?.balanceType === BalanceType.PUNCH_CARD;

  const expirationDate = membership.expirationDate
    ? t('app.payment.expires.text', {
        expirationDate: dateAndTimeFormatter.formatDate(
          membership.expirationDate.toISOString(),
        ),
      })
    : undefined;

  const { remaining, total, redemptionCost } = membership.credits || {};
  const planUsage = total
    ? t(
        isSessionBased
          ? 'app.payment.plan-usage.sessions-left'
          : 'app.payment.plan-usage.credits-left',
        {
          remaining,
          total,
        },
      )
    : undefined;

  const pricingDetails =
    planUsage && expirationDate
      ? t('app.payment.plan-usage.details', {
          planUsage,
          expirationDate,
        })
      : expirationDate ?? planUsage;

  const suffix =
    redemptionCost && !isSessionBased
      ? t('app.payment.plan-usage.credits-cost', { redemptionCost })
      : undefined;

  return {
    id: membership.id!,
    label: membership.name?.original!,
    benefitInfo: { benefit: { benefitType: getBenefitType(membership) } },
    disabled,
    disabledReason,
    suffix,
    pricingDetails,
    creditRemain: remaining || 0,
    creditCost: redemptionCost || 1,
    type: PaymentType.USE_PLAN,
  };
}

export const isDisabledMembership = ({
  membership,
  numberOfParticipants,
}: {
  membership: Membership;
  numberOfParticipants: number;
}) => {
  const isUnlimitedMembership = !membership?.credits?.remaining;
  return (
    Number(membership?.credits?.remaining) < numberOfParticipants ||
    (isUnlimitedMembership && numberOfParticipants > 1)
  );
};

export const enum DepositOrFullAmountOptionIds {
  Deposit = 'DEPOSIT_OPTION',
  FullAmount = 'FULL_AMOUNT_OPTION',
}

export const isDepositOrFullAmountOption = (option: PaymentOption): boolean =>
  option.id === DepositOrFullAmountOptionIds.Deposit ||
  option.id === DepositOrFullAmountOptionIds.FullAmount;

export const getPaymentTypeFromOption = (
  paymentOption: PaymentOption,
): PaymentMethod => {
  if (paymentOption.type === PaymentType.USE_PLAN) {
    return PaymentMethod.MEMBERSHIP;
  } else if (paymentOption.type === PaymentType.BUY_PLAN) {
    return PaymentMethod.MEMBERSHIP;
  } else if (paymentOption.type === PaymentType.BUY_PLAN_AND_BOOK) {
    return PaymentMethod.MEMBERSHIP;
  } else if (paymentOption.type === PaymentType.SUBSCRIPTION) {
    return PaymentMethod.ONLINE;
  }
  // Every SINGLE_SESSION option builder stamps paymentMethod directly — it's the
  // source of truth for ONLINE/OFFLINE (label is display text, not reliable for this).
  return paymentOption.paymentMethod ?? PaymentMethod.OFFLINE;
};

export const isDepositOrFullAmountFeatureEnabled = ({
  experiments,
  fullUpfrontPaymentAllowed,
  deposit,
  paymentTypes,
}: {
  experiments?: Experiments;
  fullUpfrontPaymentAllowed?: boolean;
  deposit: number;
  paymentTypes: PaymentMethod[];
}): boolean =>
  experiments?.enabled(ExperimentsConsts.DepositeOrFullAmountUoU) === true &&
  fullUpfrontPaymentAllowed === true &&
  deposit > 0 &&
  paymentTypes.includes(PaymentMethod.ONLINE);

export const DepositOrFullAmountTranslationKeys = {
  depositLabel:
    'app.bookings-form.payment-section.service-dropdown.pay-deposit',
  depositSubtitle:
    'app.bookings-form.payment-section.service-dropdown.pay-deposit-explanation',
  fullAmountLabel:
    'app.bookings-form.payment-section.service-dropdown.pay-full-amount',
} as const;

// Deposit option: the deposit amount shows in the suffix; the subtitle is a
// fixed explanation, followed by the discount name (when a discount applies).
export const createDepositOption = (
  depositPrice: string,
  t: TFunction,
  discountRuleName?: string,
  isCart?: boolean,
): PaymentOption => {
  return {
    id: DepositOrFullAmountOptionIds.Deposit,
    label: t(DepositOrFullAmountTranslationKeys.depositLabel),
    disabled: false,
    type: PaymentType.SINGLE_SESSION,
    paymentMethod: PaymentMethod.ONLINE,
    suffix: depositPrice,
    pricingDetails: t(DepositOrFullAmountTranslationKeys.depositSubtitle),
    discountDetails: {
      name: discountRuleName,
      isCart,
    },
  };
};

// Full amount option: the (catalog, pre-discount) price shows in the suffix.
// With a discount the subtitle is the discount name alone; without one there is
// no subtitle — the label and suffix price are self-explanatory.
export const createFullAmountOption = (
  fullPrice: string,
  t: TFunction,
  discountRuleName?: string,
  isCart?: boolean,
): PaymentOption => {
  return {
    id: DepositOrFullAmountOptionIds.FullAmount,
    label: t(DepositOrFullAmountTranslationKeys.fullAmountLabel),
    disabled: false,
    type: PaymentType.SINGLE_SESSION,
    paymentMethod: PaymentMethod.ONLINE,
    suffix: fullPrice,
    discountDetails: {
      name: discountRuleName,
      isCart,
    },
  };
};

export const createSubscriptionOption = (
  t: TFunction,
  totalPayments: number,
  formattedPrice: string,
): PaymentOption => ({
  id: ReservedPaymentOptionIds.Subscription,
  label: t('app.payment.subscription.label'),
  disabled: false,
  type: PaymentType.SUBSCRIPTION,
  pricingDetails: t('app.payment.subscription.description', {
    count: totalPayments,
  }),
  suffix: t('app.payment.summary.subscription.monthly-price', {
    price: formattedPrice,
  }),
});
